import { type Block, type LayoutFn, type Line } from "./base";
import { normalizedWidth } from "./base";
import { hl, padLine } from "./text";
import { markScrollRegion } from "./scroll-tag";

export type BoxAction = { label: string; onClick: () => void; color?: string };

type BorderTopOpts = { title?: string; color?: string; action?: BoxAction };

function borderTop(w: number, opts?: BorderTopOpts): Line {
  const width = normalizedWidth(w);
  if (width <= 0) return [];
  if (width === 1) return [{ text: "\u250c", color: opts?.color, noSelect: true }];

  const { title, color, action } = opts ?? {};
  const innerWidth = width - 2; // minus corners

  // Title prefix: "─ title " (3 chars of framing + title length)
  // Reserve 1 extra char so there's always at least one dash of fill.
  const titleBudget = innerWidth - (action ? action.label.length + 2 : 0) - 4;
  let titleStr = "";
  if (title) {
    const safeTitle = title.length > titleBudget ? title.slice(0, Math.max(0, titleBudget)) : title;
    titleStr = safeTitle.length > 0 ? "\u2500 " + safeTitle + " " : "";
  }

  const fillWidth = Math.max(0, innerWidth - titleStr.length - (action ? action.label.length + 2 : 0));

  if (!action) {
    return [{ text: "\u250c" + titleStr + hl(fillWidth) + "\u2510", color, noSelect: true }];
  }

  return [
    { text: "\u250c" + titleStr + hl(fillWidth), color, noSelect: true },
    { text: " " + action.label + " ", color: action.color ?? color, onClick: action.onClick, noSelect: true },
    { text: "\u2510", color, noSelect: true },
  ];
}

function borderBottom(w: number, color?: string): Line {
  const width = normalizedWidth(w);
  if (width <= 0) return [];
  if (width === 1) return [{ text: "\u2514", color, noSelect: true }];
  return [{ text: "\u2514" + hl(width - 2) + "\u2518", color, noSelect: true }];
}

export function lines(
  content: Line[] | ((w: number) => Line[]),
): LayoutFn {
  return (w) => {
    const ls = typeof content === "function" ? content(w) : content;
    return ls.map((line) => padLine(line, w));
  };
}

export function box(
  content: LayoutFn,
  opts?: { title?: string; borderColor?: string; action?: BoxAction },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    if (width < 2) return content(width);

    const bc = opts?.borderColor;
    const inner = width - 2;
    const contentBlock = content(inner);
    const result: Block = [];
    result.push(borderTop(width, { title: opts?.title, color: bc, action: opts?.action }));
    for (const line of contentBlock) {
      result.push([
        { text: "\u2502", color: bc, noSelect: true },
        ...padLine(line, inner),
        { text: "\u2502", color: bc, noSelect: true },
      ]);
    }
    result.push(borderBottom(width, bc));
    return result;
  };
}

export function hstack(
  children: LayoutFn[],
  opts?: { gap?: number; widths?: number[] },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const n = children.length;
    if (n === 0) return [];
    const gap = normalizedWidth(opts?.gap ?? 1);
    const totalGap = gap * (n - 1);

    let childWidths: number[];
    if (opts?.widths && opts.widths.length === n) {
      childWidths = opts.widths.map((cw) => normalizedWidth(cw));
    } else {
      const available = Math.max(0, width - totalGap);
      const base = Math.floor(available / n);
      const remainder = available - base * n;
      childWidths = children.map((_, i) => base + (i < remainder ? 1 : 0));
    }

    const blocks = children.map((child, i) => child(childWidths[i]!));
    const maxLines = Math.max(...blocks.map((b) => b.length));
    const gapStr = gap > 0 ? " ".repeat(gap) : "";

    const result: Block = [];
    for (let i = 0; i < maxLines; i++) {
      const line: Line = [];
      for (let j = 0; j < blocks.length; j++) {
        if (j > 0 && gapStr) line.push(gapStr);
        const childLine = blocks[j]![i];
        if (childLine) {
          line.push(...padLine(childLine, childWidths[j]!));
        } else {
          line.push(" ".repeat(childWidths[j]!));
        }
      }
      result.push(line);
    }
    return result;
  };
}

export function vstack(...children: LayoutFn[]): LayoutFn {
  return (w) => {
    const result: Block = [];
    for (const child of children) {
      result.push(...child(w));
    }
    return result;
  };
}

export function separator(color?: string, char?: string): LayoutFn {
  return (w) => {
    const c = char ?? "\u2500";
    return [[{ text: c.repeat(normalizedWidth(w)), color, noSelect: true }]];
  };
}

export function blank(count = 1): LayoutFn {
  return (w) => {
    const line = [" ".repeat(normalizedWidth(w))];
    const result: Block = [];
    for (let i = 0; i < count; i++) result.push(line);
    return result;
  };
}

export function divider(
  label: string,
  opts?: { color?: string; char?: string; labelColor?: string },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const c = opts?.char ?? "\u2500";
    const padded = ` ${label} `;
    if (padded.length >= width) {
      const lc = opts?.labelColor ?? opts?.color;
      return [[lc ? { text: padded.slice(0, width), color: lc } : padded.slice(0, width)]];
    }
    const remaining = width - padded.length;
    const leftLen = Math.floor(remaining / 2);
    const rightLen = remaining - leftLen;
    const lineColor = opts?.color;
    const labelColor = opts?.labelColor ?? opts?.color;
    const line: Line = [
      { text: c.repeat(leftLen), color: lineColor, noSelect: true },
      labelColor ? { text: padded, color: labelColor } : padded,
      { text: c.repeat(rightLen), color: lineColor, noSelect: true },
    ];
    return [line];
  };
}

export function grid(
  cells: LayoutFn[],
  opts: { cols: number; gap?: number; rowGap?: number },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const cols = Math.max(1, opts.cols);
    const gap = normalizedWidth(opts.gap ?? 1);
    const rowGap = normalizedWidth(opts.rowGap ?? 0);
    const totalGap = gap * (cols - 1);
    const cellWidth = Math.max(1, Math.floor((width - totalGap) / cols));
    const gapStr = gap > 0 ? " ".repeat(gap) : "";
    const result: Block = [];

    for (let rowStart = 0; rowStart < cells.length; rowStart += cols) {
      if (rowStart > 0 && rowGap > 0) {
        const emptyLine: Line = [" ".repeat(width)];
        for (let g = 0; g < rowGap; g++) result.push(emptyLine);
      }
      const rowCells = cells.slice(rowStart, rowStart + cols);
      const blocks = rowCells.map((cell) => cell(cellWidth));
      const maxLines = Math.max(0, ...blocks.map((b) => b.length));

      for (let i = 0; i < maxLines; i++) {
        const line: Line = [];
        for (let j = 0; j < cols; j++) {
          if (j > 0 && gapStr) line.push(gapStr);
          const block = blocks[j];
          if (block && block[i]) {
            line.push(...padLine(block[i]!, cellWidth));
          } else {
            line.push(" ".repeat(cellWidth));
          }
        }
        result.push(padLine(line, width));
      }
    }

    return result;
  };
}

export function scroll(
  content: LayoutFn,
  opts: { maxHeight: number; offset?: number; scrollbarColor?: string; onScroll?: (offset: number) => void },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const maxH = normalizedWidth(opts.maxHeight);
    const contentW = Math.max(1, width - 1);
    const fullBlock = content(contentW);
    const totalLines = fullBlock.length;

    if (totalLines <= maxH) {
      return fullBlock.map((line) => padLine(line, width));
    }

    const maxOffset = Math.max(0, totalLines - maxH);
    const offset = Math.min(maxOffset, Math.max(0, opts.offset ?? 0));
    const visible = fullBlock.slice(offset, offset + maxH);

    const thumbHeight = Math.max(1, Math.round((maxH / totalLines) * maxH));
    const thumbStart = Math.round((offset / maxOffset) * (maxH - thumbHeight));

    const result: Block = [];
    for (let i = 0; i < visible.length; i++) {
      const inThumb = i >= thumbStart && i < thumbStart + thumbHeight;
      const scrollChar = inThumb ? "\u2588" : "\u2591";
      const line: Line = [
        ...padLine(visible[i]!, contentW),
        { text: scrollChar, color: opts.scrollbarColor, noSelect: true },
      ];
      if (opts.onScroll) {
        markScrollRegion(line, { onScroll: opts.onScroll, totalLines, maxHeight: maxH, offset });
      }
      result.push(line);
    }

    return result;
  };
}

export function padding(
  content: LayoutFn,
  opts?: { x?: number; y?: number; top?: number; bottom?: number; left?: number; right?: number },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const t = normalizedWidth(opts?.top ?? opts?.y ?? 0);
    const b = normalizedWidth(opts?.bottom ?? opts?.y ?? 0);
    const l = normalizedWidth(opts?.left ?? opts?.x ?? 0);
    const r = normalizedWidth(opts?.right ?? opts?.x ?? 0);
    const innerW = Math.max(1, width - l - r);
    const block = content(innerW);
    const result: Block = [];
    const emptyLine: Line = [" ".repeat(width)];
    for (let i = 0; i < t; i++) result.push(emptyLine);
    const leftPad = l > 0 ? " ".repeat(l) : "";
    for (const line of block) {
      const padded: Line = leftPad ? [leftPad, ...line] : line;
      result.push(padLine(padded, width));
    }
    for (let i = 0; i < b; i++) result.push(emptyLine);
    return result;
  };
}
